import React, { useEffect, useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, UserIcon, ArchiveIcon, BanIcon, CheckCircleIcon, SearchIcon, FilterIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Modal } from '../ui/Modal';
import { Table } from '../ui/Table';
import { Badge } from '../ui/Badge';
import { User, UserRole } from '../../types';
import { supabase } from '../../utils/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { getRoleName, generateId } from '../../utils/helpers';
type UserStatus = 'active' | 'inactive' | 'archived';
interface UserWithStatus extends User {
  status: UserStatus;
  gender?: string;
}
export function UserManagement() {
  const {
    isConnectedToSupabase
  } = useAuth();
  const {
    roles
  } = useData();
  const [users, setUsers] = useState<UserWithStatus[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserWithStatus[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserWithStatus | null>(null);
  // Filter states
  const [statusFilter, setStatusFilter] = useState<UserStatus | 'all'>('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'administrador' as UserRole,
    gender: '' as string
  });
  const [saveMessage, setSaveMessage] = useState('');
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // Build role options
  const roleOptions = [{
    value: 'administrador',
    label: 'Administrador'
  }, ...roles.map(r => ({
    value: r.name,
    label: r.displayName || r.name
  }))];
  // Load user statuses from localStorage
  const getUserStatuses = (): Record<string, UserStatus> => {
    try {
      const saved = localStorage.getItem('savidna-user-statuses');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  };
  const saveUserStatus = (userId: string, status: UserStatus) => {
    const statuses = getUserStatuses();
    statuses[userId] = status;
    localStorage.setItem('savidna-user-statuses', JSON.stringify(statuses));
  };
  // Load users from Supabase
  const loadUsers = async () => {
    setIsLoadingUsers(true);
    const statuses = getUserStatuses();
    if (isConnectedToSupabase) {
      try {
        const {
          data,
          error
        } = await supabase.from('users').select('*').order('created_at', {
          ascending: false
        });
        if (data && !error) {
          const mappedUsers: UserWithStatus[] = data.map(u => ({
            id: u.id,
            email: u.email,
            password: u.password || '',
            name: u.name,
            role: u.role as UserRole,
            gender: u.gender || '',
            avatar: u.avatar,
            language: u.language || 'es',
            timezone: u.timezone || 'America/Santo_Domingo',
            createdAt: new Date(u.created_at),
            // FIXED: Prioritize Supabase status over localStorage
            status: u.status as UserStatus || statuses[u.id] || 'active'
          }));
          setUsers(mappedUsers);
          // Sync localStorage with Supabase status
          const updatedStatuses = {
            ...statuses
          };
          mappedUsers.forEach(user => {
            if (user.status !== 'active') {
              updatedStatuses[user.id] = user.status;
            }
          });
          localStorage.setItem('savidna-user-statuses', JSON.stringify(updatedStatuses));
        }
      } catch (e) {
        console.error('Error cargando usuarios:', e);
      }
    } else {
      // Fallback for local development if needed, though usually empty initially
      // For now we rely on the auth context user or empty array
      setUsers([]);
    }
    setIsLoadingUsers(false);
  };
  useEffect(() => {
    loadUsers();
  }, [isConnectedToSupabase]);
  // Filter logic
  useEffect(() => {
    let result = users;
    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(u => u.status === statusFilter);
    }
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(u => u.name.toLowerCase().includes(term) || u.email.toLowerCase().includes(term) || getRoleName(u.role).toLowerCase().includes(term));
    }
    setFilteredUsers(result);
  }, [users, statusFilter, searchTerm]);
  const handleOpenModal = (user?: UserWithStatus) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
        gender: user.gender || ''
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'administrador',
        gender: ''
      });
    }
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'administrador',
      gender: ''
    });
  };
  const handleSaveUser = async () => {
    if (!formData.name || !formData.email) {
      alert('Por favor completa todos los campos requeridos');
      return;
    }
    if (!editingUser && !formData.password) {
      alert('La contraseña es requerida para nuevos usuarios');
      return;
    }
    setIsSaving(true);
    if (isConnectedToSupabase) {
      try {
        if (editingUser) {
          // Update in Supabase
          const updateData: any = {
            name: formData.name,
            email: formData.email.toLowerCase().trim(),
            role: formData.role,
            gender: formData.gender || null
          };
          if (formData.password) {
            updateData.password = formData.password;
          }
          const {
            error
          } = await supabase.from('users').update(updateData).eq('id', editingUser.id);
          if (error) {
            setIsSaving(false);
            alert('Error actualizando usuario: ' + error.message);
            return;
          }
          // Update local state immediately (optimistic)
          setUsers(prev => prev.map(u => u.id === editingUser.id ? {
            ...u,
            name: formData.name,
            email: formData.email.toLowerCase().trim(),
            role: formData.role as UserRole,
            gender: formData.gender || ''
          } : u));
          setSaveMessage('✅ Usuario actualizado');
        } else {
          // Create in Supabase
          const newId = crypto.randomUUID();
          const {
            error
          } = await supabase.from('users').insert({
            id: newId,
            email: formData.email.toLowerCase().trim(),
            password: formData.password,
            name: formData.name,
            role: formData.role,
            gender: formData.gender || null,
            language: 'es',
            timezone: 'America/Santo_Domingo'
          });
          if (error) {
            setIsSaving(false);
            alert('Error creando usuario: ' + error.message);
            return;
          }
          // Add to local state immediately
          const statuses = getUserStatuses();
          setUsers(prev => [{
            id: newId,
            name: formData.name,
            email: formData.email.toLowerCase().trim(),
            password: '',
            role: formData.role as UserRole,
            gender: formData.gender || '',
            language: 'es',
            timezone: 'America/Santo_Domingo',
            createdAt: new Date(),
            status: (statuses[newId] || 'active') as UserStatus
          }, ...prev]);
          setSaveMessage('✅ Usuario creado');
        }
      } catch (e: any) {
        setIsSaving(false);
        alert('Error: ' + (e.message || 'Error desconocido'));
        return;
      }
    } else {
      setIsSaving(false);
      alert('Se requiere conexión a Supabase para gestionar usuarios');
      return;
    }
    setIsSaving(false);
    handleCloseModal();
    setTimeout(() => setSaveMessage(''), 3000);
  };
  const handleStatusChange = (userId: string, newStatus: UserStatus) => {
    saveUserStatus(userId, newStatus);
    setUsers(prev => prev.map(u => u.id === userId ? {
      ...u,
      status: newStatus
    } : u));
    // Persist to Supabase
    supabase.from('users').update({
      status: newStatus
    }).eq('id', userId).then(({
      error
    }) => {
      if (error) console.error('Error updating user status in Supabase:', error);else console.log('✅ User status updated in Supabase:', newStatus);
    });
    const actionName = newStatus === 'active' ? 'activado' : newStatus === 'inactive' ? 'desactivado' : 'archivado';
    setSaveMessage(`Usuario ${actionName} correctamente`);
    setTimeout(() => setSaveMessage(''), 3000);
  };
  const getRoleBadgeVariant = (role: UserRole) => {
    if (role === 'administrador') return 'primary';
    return 'info';
  };
  const getStatusBadgeVariant = (status: UserStatus) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'warning';
      case 'archived':
        return 'default';
    }
  };
  const getStatusLabel = (status: UserStatus) => {
    switch (status) {
      case 'active':
        return 'Activo';
      case 'inactive':
        return 'Inactivo';
      case 'archived':
        return 'Archivado';
    }
  };
  const columns = [{
    key: 'name',
    header: 'Usuario',
    render: (user: UserWithStatus) => <div className="flex items-center gap-3" data-id="element-4146">
          {user.avatar ? <img src={user.avatar} alt={user.name} className={`w-10 h-10 rounded-full object-cover border-2 ${user.status === 'active' ? 'border-green-400' : 'border-gray-200 dark:border-gray-600'}`} data-id="element-4147" /> : <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${user.status === 'active' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`} data-id="element-4148">
              {user.name.charAt(0).toUpperCase()}
            </div>}
          <div data-id="element-4149">
            <div className={`font-medium ${user.status === 'archived' ? 'text-gray-500 line-through' : 'text-gray-900 dark:text-white'}`} data-id="element-4150">
              {user.name}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4151">
              {user.email}
            </div>
          </div>
        </div>
  }, {
    key: 'role',
    header: 'Rol',
    render: (user: UserWithStatus) => <Badge variant={getRoleBadgeVariant(user.role)} data-id="element-4152">
          {getRoleName(user.role)}
        </Badge>
  }, {
    key: 'status',
    header: 'Estado',
    render: (user: UserWithStatus) => <Badge variant={getStatusBadgeVariant(user.status)} data-id="element-4153">
          {getStatusLabel(user.status)}
        </Badge>
  }, {
    key: 'actions',
    header: 'Acciones',
    render: (user: UserWithStatus) => <div className="flex items-center gap-1" data-id="element-4154">
          <button onClick={() => handleOpenModal(user)} className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Editar usuario" data-id="element-4155">
            <EditIcon className="w-4 h-4" data-id="element-4156" />
          </button>

          {user.status === 'active' ? <button onClick={() => handleStatusChange(user.id, 'inactive')} className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Desactivar usuario" data-id="element-4157">
              <BanIcon className="w-4 h-4" data-id="element-4158" />
            </button> : <button onClick={() => handleStatusChange(user.id, 'active')} className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Activar usuario" data-id="element-4159">
              <CheckCircleIcon className="w-4 h-4" data-id="element-4160" />
            </button>}

          {user.status !== 'archived' ? <button onClick={() => handleStatusChange(user.id, 'archived')} className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors" title="Archivar usuario" data-id="element-4161">
              <ArchiveIcon className="w-4 h-4" data-id="element-4162" />
            </button> : <button onClick={() => handleStatusChange(user.id, 'active')} className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar usuario" data-id="element-4163">
              <PlusIcon className="w-4 h-4" data-id="element-4164" />
            </button>}
        </div>
  }];
  return <div className="space-y-6" data-id="element-4165">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-4166">
        <div data-id="element-4167">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-4168">
            <UserIcon className="w-6 h-6 text-teal-600 dark:text-teal-400" data-id="element-4169" />
            Gestión de Usuarios
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-4170">
            {users.length} usuarios registrados en el sistema
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} data-id="element-4171">
          <PlusIcon className="w-4 h-4" data-id="element-4172" />
          Nuevo Usuario
        </Button>
      </div>

      {saveMessage && <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400 text-sm animate-in fade-in slide-in-from-top-2" data-id="element-4173">
          {saveMessage}
        </div>}

      {/* Filters */}
      <Card className="p-4" data-id="element-4174">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between" data-id="element-4175">
          <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-full md:w-auto" data-id="element-4176">
            {(['active', 'inactive', 'archived', 'all'] as const).map(status => <button key={status} onClick={() => setStatusFilter(status)} className={`
                  px-4 py-1.5 text-sm font-medium rounded-md transition-all flex-1 md:flex-none capitalize
                  ${statusFilter === status ? 'bg-white dark:bg-gray-700 text-teal-600 dark:text-teal-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}
                `} data-id="element-4177">
                  {status === 'all' ? 'Todos' : getStatusLabel(status)}
                </button>)}
          </div>

          <div className="relative w-full md:w-64" data-id="element-4178">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-4179" />
            <input type="text" placeholder="Buscar usuarios..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all" data-id="element-4180" />
          </div>
        </div>
      </Card>

      {/* Users Table */}
      <Card className="overflow-hidden" data-id="element-4181">
        {isLoadingUsers ? <div className="p-12 text-center text-gray-500" data-id="element-4182">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto mb-3" data-id="element-4183"></div>
            Cargando usuarios...
          </div> : <Table columns={columns} data={filteredUsers} keyExtractor={user => user.id} emptyMessage="No se encontraron usuarios con los filtros seleccionados" data-id="element-4184" />}
      </Card>

      {/* User Modal */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={editingUser ? 'Editar Usuario' : 'Nuevo Usuario'} size="md" icon={<UserIcon className="w-5 h-5" data-id="element-4186" />} accentColor="sky" data-id="element-4185">
        <div className="space-y-6" data-id="element-4187">
          {/* Personal Info Section */}
          <div data-id="element-4188">
            <div className="flex items-center gap-2 mb-4" data-id="element-4189">
              <span className="w-1.5 h-5 rounded-full bg-teal-500" data-id="element-4190"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-4191">
                Información Personal
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-4192">
              <Input label="Nombre Completo" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} placeholder="Ej: Juan Pérez" data-id="element-4193" />
              <div className="grid grid-cols-2 gap-4" data-id="element-4194">
                <Input label="Correo Electrónico" type="email" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} placeholder="usuario@ejemplo.com" data-id="element-4195" />
                <Select label="Género" options={[{
                value: 'masculino',
                label: 'Masculino'
              }, {
                value: 'femenino',
                label: 'Femenino'
              }, {
                value: 'otro',
                label: 'Otro'
              }]} value={formData.gender} onChange={value => setFormData({
                ...formData,
                gender: value
              })} data-id="element-4196" />
              </div>
            </div>
          </div>

          {/* Access & Security Section */}
          <div data-id="element-4197">
            <div className="flex items-center gap-2 mb-4" data-id="element-4198">
              <span className="w-1.5 h-5 rounded-full bg-amber-500" data-id="element-4199"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-4200">
                Acceso y Seguridad
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-4201">
              <Input label={editingUser ? 'Nueva Contraseña (opcional)' : 'Contraseña'} type="password" value={formData.password} onChange={e => setFormData({
              ...formData,
              password: e.target.value
            })} placeholder="••••••••" data-id="element-4202" />
              <Select label="Rol del Usuario" options={roleOptions} value={formData.role} onChange={value => setFormData({
              ...formData,
              role: value as UserRole
            })} data-id="element-4203" />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-4204">
            <Button variant="secondary" onClick={handleCloseModal} disabled={isSaving} data-id="element-4205">
              Cancelar
            </Button>
            <Button onClick={handleSaveUser} isLoading={isSaving} data-id="element-4206">
              {editingUser ? 'Actualizar' : 'Crear'} Usuario
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}